import React, { useEffect, useState } from 'react';
import { Box, Grid } from '@mui/material';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import PageHeader from '../../../components/PageHeader/PageHeader';
import MonthYearSelector from '../../../components/Reports/Sales/MonthYearSelector';
import SummaryCards from '../../../components/Reports/SummaryCards';
import ClientsPerMonthChart from '../../../components/Reports/Clients/ClientsPerMonthChart';
import FrequentClientsTable from '../../../components/Reports/Clients/FrequentClientsTable';
import TopSpendersTable from '../../../components/Reports/Clients/TopSpendersTable';
import { getClientDashboardData } from '../../../services/ReportsClientsService';

import PeopleIcon from '@mui/icons-material/People';
import ReplayIcon from '@mui/icons-material/Replay';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import { formatMoney } from '../../../utils/currency';

import useReportCurrency from '../../../hooks/useReportCurrency';
import ReportCurrencyPicker from '../../../components/Currency/ReportCurrencyPicker';
const ClientsReports = () => {
  const [reportCurrency, setReportCurrency] = useReportCurrency();
  const { t } = useTranslation();
  const [month, setMonth] = useState(dayjs().month() + 1);
  const [year, setYear] = useState(dayjs().year());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      const result = await getClientDashboardData({ month, year });
      setData(result);
      setLoading(false);
    };
    fetch();
  }, [month, year, reportCurrency]);

  const current = data?.summary.newClients.current;
  const previous = data?.summary.newClients.previous;
  const percentChange = previous > 0 ? ((current - previous) / previous) * 100 : 0;

  const summaryItems = data?.summary
    ? [
        {
          title: 'Nuevos clientes',
          icon: <PeopleIcon color="primary" fontSize="large" />,
          value: `${current}`,
          subtitle: `${percentChange >= 0 ? '▲' : '▼'} ${Math.abs(percentChange).toFixed(1)}% vs mes anterior`,
          subtitleColor: percentChange >= 0 ? 'success.main' : 'error.main',
        },
        {
          title: 'Frecuencia promedio',
          icon: <CalendarTodayIcon color="secondary" fontSize="large" />,
          value: `${data.summary.averageFrequencyDays.toFixed(1)} días`,
          subtitle: 'Entre compras del mismo cliente',
        },
        {
          title: 'Tasa de retención',
          icon: <ReplayIcon color="success" fontSize="large" />,
          value: `${data.summary.retentionRate.toFixed(1)}%`,
          subtitle: 'Clientes que volvieron a comprar',
        },
        {
          title: 'Ingreso promedio por cliente',
          icon: <MonetizationOnIcon color="action" fontSize="large" />,
          value: `${formatMoney(data.summary.avgRevenuePerClient)}`,
        },
      ]
    : [];

  return (
    <Box p={3}>
      <Grid container spacing={2} alignItems="center" justifyContent="space-between">
        <Grid item>
          <PageHeader title={t('clients')} subtitle={t('Estadísticas de comportamiento de clientes')} />
      <ReportCurrencyPicker currency={reportCurrency} onChange={setReportCurrency} />
        </Grid>
        <Grid item>
          <MonthYearSelector
            month={month}
            setMonth={setMonth}
            year={year}
            setYear={setYear}
          />
        </Grid>
      </Grid>

      <Box mt={2}>
        <SummaryCards items={summaryItems} loading={loading} />
      </Box>

      <Grid container spacing={2} mt={0}>
        <Grid item xs={12}>
          <ClientsPerMonthChart
            data={data?.clientsPerMonth || []}
            loading={loading}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} mt={4} mb={6}>
        <Grid item xs={12} md={6}>
          <FrequentClientsTable
            data={data?.frequentClients || []}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TopSpendersTable
            data={data?.topSpenders || []}
            loading={loading}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default ClientsReports;
